import {
  AttachMoney as AttachMoneyIcon,
  Inventory as InventoryIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  ShoppingCart as ShoppingCartIcon,
  LocalMall as LocalMallIcon,
  AccountBalanceWallet as WalletIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useDashboardOverview, useRefreshAnalytics } from "../../hooks/useAnalytics";
import { AnalyticsFilter, RecentOrder } from "../../types/analytics";
import KpiStatCard from "./analytics/KpiStatCard";
import PeriodSelector from "./analytics/PeriodSelector";
import RevenueChart from "./analytics/RevenueChart";
import PieBreakdown from "./analytics/PieBreakdown";
import StatusBreakdownChart from "./analytics/StatusBreakdownChart";
import TopProductsList from "./analytics/TopProductsList";

// Darken light colors so they're readable as text on white backgrounds
const getReadableTextColor = (hex: string): string => {
  if (!hex || !hex.startsWith("#")) return hex;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  // Relative luminance (perceived brightness)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  if (luminance > 0.55) {
    // Darken by 40% for readability
    const darken = (c: number) => Math.round(c * 0.6);
    return `rgb(${darken(r)}, ${darken(g)}, ${darken(b)})`;
  }
  return hex;
};

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<AnalyticsFilter>({ period: "30d" });

  const { data: overviewResponse, isLoading } = useDashboardOverview(filter);
  const overview = overviewResponse?.data;
  const refreshMutation = useRefreshAnalytics();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Welcome Section + Period Selector */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 4,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h3"
            fontWeight={800}
            sx={{
              mb: 1,
              background:
                "linear-gradient(135deg, #6366f1 0%, #ec4899 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {t("admin.dashboard.welcomeBack")}
          </Typography>
          <Typography variant="h6" color="text.secondary" fontWeight={500}>
            {t("admin.dashboard.storeOverview")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PeriodSelector filter={filter} onChange={setFilter} />
          <Tooltip title={t("admin.analytics.refreshData")}>
            <IconButton
              onClick={() => refreshMutation.mutate()}
              disabled={refreshMutation.isPending}
              sx={{
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <RefreshIcon
                sx={{
                  animation: refreshMutation.isPending
                    ? "spin 1s linear infinite"
                    : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* KPI Stats Cards */}
      {overview && (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiStatCard
                title={t("admin.analytics.totalSales")}
                data={overview.totalSales}
                prefix="$"
                icon={<AttachMoneyIcon />}
                color="#10b981"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiStatCard
                title={t("admin.analytics.totalOrders")}
                data={overview.totalOrders}
                icon={<ShoppingCartIcon />}
                color="#6366f1"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiStatCard
                title={t("admin.analytics.totalCustomers")}
                data={overview.totalCustomers}
                icon={<PeopleIcon />}
                color="#f59e0b"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiStatCard
                title={t("admin.analytics.avgOrderValue")}
                data={overview.averageOrderValue}
                prefix="$"
                icon={<LocalMallIcon />}
                color="#ec4899"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiStatCard
                title={t("admin.analytics.totalItemsSold")}
                data={overview.totalItemsSold}
                icon={<InventoryIcon />}
                color="#8b5cf6"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiStatCard
                title={t("admin.analytics.totalEarnings")}
                data={overview.totalEarnings}
                prefix="$"
                icon={<WalletIcon />}
                color="#06b6d4"
              />
            </Grid>
          </Grid>

          {/* Charts Section */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} lg={8}>
              <RevenueChart
                title={t("admin.dashboard.revenueOverview")}
                revenueData={
                  overview.topProducts.length > 0
                    ? overview.recentOrders
                        .slice()
                        .reverse()
                        .reduce(
                          (acc: { label: string; date: string; value: number; count: number }[], order: RecentOrder) => {
                            const date = new Date(
                              order.createdAt
                            ).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                            });
                            const existing = acc.find(
                              (a) => a.label === date
                            );
                            if (existing) {
                              existing.value += order.finalTotal;
                              existing.count += 1;
                            } else {
                              acc.push({
                                label: date,
                                date: order.createdAt,
                                value: order.finalTotal,
                                count: 1,
                              });
                            }
                            return acc;
                          },
                          [] as {
                            label: string;
                            date: string;
                            value: number;
                            count: number;
                          }[]
                        )
                    : []
                }
                height={320}
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <TopProductsList
                title={t("admin.dashboard.topProducts")}
                products={overview.topProducts}
              />
            </Grid>
          </Grid>

          {/* Status Breakdowns */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <StatusBreakdownChart
                title={t("admin.analytics.orderStatusBreakdown")}
                data={overview.orderStatusBreakdown}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <StatusBreakdownChart
                title={t("admin.analytics.itemStatusBreakdown")}
                data={overview.itemStatusBreakdown}
              />
            </Grid>
          </Grid>

          {/* Recent Orders */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <Typography variant="h5" fontWeight={700}>
                      {t("admin.dashboard.recentOrders")}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      endIcon={<KeyboardArrowRightIcon />}
                      onClick={() => navigate("/admin/orders")}
                      sx={{
                        borderRadius: 3,
                        fontWeight: 600,
                        borderColor: "#e5e7eb",
                        color: "#6b7280",
                        "&:hover": {
                          borderColor: "#6366f1",
                          color: "#6366f1",
                          background: "rgba(99, 102, 241, 0.04)",
                        },
                      }}
                    >
                      {t("common.viewAll")}
                    </Button>
                  </Box>

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow
                          sx={{
                            background:
                              "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                          }}
                        >
                          <TableCell sx={{ fontWeight: 700 }}>
                            {t("admin.dashboard.orderId")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            {t("admin.dashboard.customer")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            {t("admin.dashboard.amount")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            {t("admin.dashboard.status")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            {t("admin.dashboard.items")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            {t("admin.dashboard.date")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {overview.recentOrders.map((order: RecentOrder) => (
                          <TableRow
                            key={order.id}
                            sx={{
                              cursor: "pointer",
                              "&:hover": {
                                background: "#f8fafc",
                              },
                            }}
                            onClick={() =>
                              navigate(`/admin/orders/${order.id}`)
                            }
                          >
                            <TableCell>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color="#6366f1"
                              >
                                {order.orderNumber}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Avatar
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    fontSize: "11px",
                                    background:
                                      "linear-gradient(135deg, #6366f1 0%, #818cf8 100%)",
                                    fontWeight: 700,
                                  }}
                                >
                                  {order.customerName
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")
                                    .slice(0, 2)}
                                </Avatar>
                                <Typography variant="body2" fontWeight={500}>
                                  {order.customerName}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                color="#10b981"
                              >
                                ${order.finalTotal.toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={order.statusName}
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "11px",
                                  height: 22,
                                  backgroundColor: order.statusColor
                                    ? `${order.statusColor}20`
                                    : undefined,
                                  color: order.statusColor
                                    ? getReadableTextColor(order.statusColor)
                                    : undefined,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {order.itemCount}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {new Date(
                                  order.createdAt
                                ).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {!overview && !isLoading && (
        <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
          <Typography variant="h6">
            {t("admin.analytics.noDataAvailable")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
